// Project content. Add `src` (image URL), `embed` (Vimeo/YouTube embed URL) or `loop` (mp4 URL) to any media item to replace its placeholder.

const RAW = [
  {
    slug: "marshall-aged-to-be-loud",
    no: "01",
    title: "Marshall — Aged to Be Loud",
    titleLines: ["Marshall —", "Aged to Be Loud"],
    category: "Brand Identity / Packaging / Experiential Concept",
    tag: "Speculative",
    note: "Speculative project. Not commissioned by, affiliated with or endorsed by Marshall.",
    summary:
      "A speculative brand extension imagining Marshall entering the whisky market, combining the energy of rock music with premium spirits.",
    highlight: "Golden Ducky Winner 2026",
    meta: [
      { label: "Discipline", value: "Brand Identity / Packaging / Experiential Concept" },
      { label: "Project type", value: "Speculative brand extension" },
      { label: "Recognition", value: "Golden Ducky Competition Winner, 2026" },
      { label: "Impact", value: "Led to a one-month internship at Turner Duckworth" },
    ],
    cover: { desc: "Cover — whisky packaging key visual", ratio: "4/5", src: "/work/marshall/04.webp", alt: "Marshall Original Tone whisky bottle under stage spotlights" },
    hero: { desc: "Hero — bottle & packaging key visual", ratio: "16/9", src: "/work/marshall/01.webp", alt: "Marshall Signature Series whisky bottles in rock-and-roll interiors with the Marshall script logo" },
    sections: [
      {
        id: "overview",
        title: "Overview",
        body: [
          "A speculative brand extension imagining Marshall entering the whisky market, combining the energy of rock music with premium spirits.",
          "The project includes original brand identity, whisky packaging, limited artist-inspired editions, digital applications and a sound-reactive live activation concept.",
        ],
      },
      {
        id: "challenge",
        title: "The Challenge",
        statement: "What if Marshall made whisky?",
        body: ["Translating sound into flavour through iconic guitar tones."],
        layout: "full",
        media: [
          { desc: "The question — what if Marshall made whisky?", ratio: "16/9", src: "/work/marshall/02.webp", alt: "Concert crowd under amber light with the line: What if Marshall made whisky? Translating sound into flavour through iconic guitar tones" },
        ],
      },
      {
        id: "concept",
        title: "The Concept",
        body: ["Each bottle represents a different sound profile: tone, translated into flavour, translated into experience."],
        layout: "full",
        media: [
          { desc: "Concept — tone, flavour, experience", ratio: "16/9", src: "/work/marshall/03.webp", alt: "Three bottle silhouettes in purple, amber and white labelled Tone, Flavour and Experience" },
        ],
      },
      {
        id: "identity",
        title: "Brand Identity",
        body: ["Original brand identity for the speculative whisky extension, pairing the Amplify script with Bebas Neue."],
        layout: "stack",
        media: [
          { desc: "Brand identity — logo lockup", ratio: "16/9", src: "/work/marshall/08.webp", alt: "Marshall script logo on black with the line Aged to Be Loud" },
          { desc: "Typography — Amplify", ratio: "2/1", src: "/work/marshall/type-amplify.webp", alt: "Amplify script typeface specimen in white on black, showing lowercase and uppercase alphabets" },
          { desc: "Typography — Bebas Neue", ratio: "2/1", src: "/work/marshall/type-bebas-neue.webp", alt: "Bebas Neue specimen in white on black, showing Thin, Light, Book, Regular and Bold weights" },
        ],
      },
      {
        id: "packaging",
        title: "Packaging",
        body: ["Whisky packaging designed for the extension, developed from hand sketches to a flat bottle layout and a 3D model."],
        layout: "stack2",
        media: [
          { desc: "Packaging — Original Tone bottle", ratio: "16/9", src: "/work/marshall/04.webp", alt: "Marshall Original Tone whisky bottle with amplifier-style control knobs, on an empty spotlit stage" },
          { desc: "Packaging — bottle sketches", ratio: "1153/658", src: "/work/marshall/bottle-sketches.webp", blend: true, alt: "Seven hand-drawn bottle sketches in black, grey and amber exploring amplifier-inspired shapes, knobs and a numbered dial cap" },
          { desc: "Packaging — bottle layout", ratio: "1151/655", src: "/work/marshall/bottle-layout.webp", blend: true, alt: "Black line layout of the bottle front and both sides, with the Original Tone label, Gain, Tone and Level knobs and a numbered cap" },
          { desc: "Packaging — 3D bottle model", ratio: "1152/654", src: "/work/marshall/bottle-3d-model.webp", alt: "Grey 3D model of the bottle from front, side and back, showing the embossed label, dial cap and knurled base" },
        ],
      },
      {
        id: "editions",
        title: "Artist Editions",
        body: ["Limited artist-inspired editions in the Signature Series: the Slash Edition and the Hendrix Edition, each with its own label artwork and flavour dials."],
        layout: "editions",
        media: [
          { desc: "Artist editions line-up", ratio: "16/9", src: "/work/marshall/05.webp", alt: "Three Marshall whisky bottles — Original Tone, Slash and Hendrix editions — on a stage with guitars and amplifiers" },
          { desc: "Slash Edition — studio shot", ratio: "4/5", src: "/work/marshall/slash-studio.webp", alt: "Slash Edition bottle in black and gold with a silhouette of Slash and Body, Tone and Finish dials reading Oak, Leather, Smoke" },
          { desc: "Hendrix Edition — studio shot", ratio: "4/5", src: "/work/marshall/hendrix-studio.webp", alt: "Hendrix Edition bottle with a purple and orange psychedelic portrait and Distortion, Heat and Feedback dials reading Smoke, Spice, Fire" },
          { desc: "Slash Edition — in context", ratio: "3/5", src: "/work/marshall/slash-lifestyle.webp", alt: "Slash Edition bottle on a red rug beside a tan leather sofa and a Marshall speaker" },
          { desc: "Hendrix Edition — in context", ratio: "3/5", src: "/work/marshall/hendrix-lifestyle.webp", alt: "Hendrix Edition bottle on a worn wooden chair against a blue wall, with CDs on the rug below" },
        ],
      },
      {
        id: "digital",
        title: "Digital Applications",
        body: ["Digital applications extending the identity."],
        layout: "full",
        media: [
          { desc: "Digital — social and website", ratio: "16/9", src: "/work/marshall/06.webp", alt: "Two phones showing Marshall whisky Instagram posts beside a laptop showing the product website" },
        ],
      },
      {
        id: "activation",
        title: "Live Activation",
        statement: "Earn your sound.",
        body: ["A live installation where sound becomes reward. Participants shout, sing or cheer into a Marshall sound machine, which measures volume in real time. Hit the required decibel level and unlock a bottle of Marshall whisky."],
        layout: "full",
        media: [
          { desc: "Live activation — Earn Your Sound", ratio: "16/9", src: "/work/marshall/07.webp", alt: "A fan shouting beside a Marshall sound machine whose volume meter reads Unlocked — Make noise, unlock flavours" },
        ],
      },
      {
        id: "outcome",
        title: "Outcome",
        statement: "Winner of the Golden Ducky Competition, 2026.",
        body: ["The winning project led to a one-month internship at Turner Duckworth."],
      },
    ],
  },
  {
    slug: "broken-streetwear-identity",
    no: "02",
    title: "BROKEN — Streetwear Brand Identity",
    titleLines: ["BROKEN —", "Streetwear Identity"],
    category: "Brand Identity / Fashion / Apparel",
    tag: "University project",
    note: "Independently developed brand identity, originally created as a university project.",
    summary:
      "An independently developed streetwear brand identity, from logo and visual system to apparel and real-world photography.",
    highlight: "Brand purchased by a third party",
    meta: [
      { label: "Discipline", value: "Brand Identity / Fashion / Apparel" },
      { label: "Project type", value: "University project, independently developed" },
      { label: "Deliverables", value: "Logo, visual system, typography, patterns, apparel, promotional graphics" },
      { label: "Impact", value: "The complete brand was subsequently purchased by a third party" },
    ],
    cover: { desc: "Cover — torn-print hoodie", ratio: "4/5", src: "/work/broken/hoodie-torn.webp", blend: true, alt: "BROKEN hoodie with an all-over torn-paper print in black and grey and the four-heart clover mark on the chest" },
    hero: { desc: "Hero — BROKEN posters", ratio: "16/10", src: "/work/broken/posters.webp", alt: "Two BROKEN promotional posters pasted on a concrete wall — a red-and-black silhouette poster and a repeating BROKEN wordmark poster with a glass heart" },
    sections: [
      {
        id: "overview",
        title: "Overview",
        body: [
          "An independently developed streetwear brand identity originally created as a university project.",
          "Includes logo, visual system, typography, graphic patterns, hoodie and T-shirt designs, promotional graphics and real-world apparel photography.",
        ],
      },
      {
        id: "concept",
        title: "Brand Concept",
        body: ["Moodboard and early broken-heart sketches."],
        layout: "asym",
        media: [
          { desc: "Brand concept — sketches", ratio: "3/4", src: "/work/broken/sketches.webp", blend: true, alt: "Pencil sketches of broken hearts — a heart torn by two hands, a cracked heart on a playing card, a shattered frame and a heartbeat line" },
          { desc: "Brand concept — moodboard", ratio: "3/4", src: "/work/broken/moodboard.webp", alt: "Black-and-white moodboard of solitary figures, birds over the sea, a figure at a window, a couple on a cracked frozen lake and a burning letter" },
        ],
      },
      {
        id: "identity",
        title: "Visual Identity",
        body: ["Logo and visual system."],
        layout: "asym",
        media: [
          { desc: "Visual identity — horizontal logo", ratio: "4/3", src: "/work/broken/logo-horizontal.webp", alt: "BROKEN horizontal logo — a four-heart clover mark with zig-zag cracks beside the BROKEN wordmark, in black" },
          { desc: "Visual identity — stacked colour logo", ratio: "4/5", src: "/work/broken/logo-stacked.webp", dark: true, alt: "BROKEN stacked logo — red cracked-heart clover mark above a pale olive BROKEN wordmark" },
        ],
      },
      {
        id: "typography",
        title: "Typography & Graphics",
        body: ["Typography and graphic patterns. The wordmark system is set in Open Sauce Sans, a typeface by Creative Sauce."],
        layout: "asym",
        media: [
          { desc: "Graphic pattern — repeating clover mark", ratio: "4/3", src: "/work/broken/pattern.webp", blend: true, alt: "Repeating pattern of red cracked-heart clover marks in staggered columns" },
          { desc: "Typography — Open Sauce Sans specimen", ratio: "1131/1600", src: "/work/broken/type-specimen.webp", blend: true, alt: "Type specimen of Open Sauce Sans showing the uppercase alphabet A to Z in black" },
        ],
      },
      {
        id: "apparel",
        title: "Apparel",
        body: ["Hoodie and T-shirt designs."],
        layout: "pair",
        media: [
          { desc: "Apparel — wordmark hoodie", ratio: "1/1", src: "/work/broken/hoodie-wordmark.webp", blend: true, alt: "Washed black BROKEN hoodie with the red clover mark and BROKEN wordmark on the chest" },
          { desc: "Apparel — spray-print hoodie", ratio: "1/1", src: "/work/broken/hoodie-spray.webp", blend: true, alt: "Washed black hoodie with a faded white spray print and a small red clover mark" },
          { desc: "Apparel — torn-print hoodie", ratio: "1/1", src: "/work/broken/hoodie-torn.webp", blend: true, alt: "Hoodie with an all-over torn-paper print and the black clover mark" },
          { desc: "Apparel — 'I gave it a shot' T-shirt", ratio: "1/1", src: "/work/broken/tee-gave-it-a-shot.webp", blend: true, alt: "Black cropped T-shirt printed with a red heart pierced by a bullet hole and the line I gave it a shot" },
        ],
      },
      {
        id: "photography",
        title: "Photography",
        body: ["Real-world apparel photography."],
        layout: "duo",
        media: [
          { desc: "Photography — hoodie front", ratio: "3/4", src: "/work/broken/photo-front.webp", alt: "Model in a navy BROKEN hoodie with the small chest logo, standing in an underground car park" },
          { desc: "Photography — hoodie back", ratio: "3/4", src: "/work/broken/photo-back.webp", alt: "Model seen from behind wearing a black BROKEN hoodie with a large red clover mark and BROKEN wordmark across the back" },
        ],
      },
      {
        id: "outcome",
        title: "Outcome",
        statement: "The complete brand was subsequently purchased by a third party.",
        body: [],
      },
    ],
  },
  {
    slug: "hsbc-the-next-frontier",
    no: "03",
    title: "HSBC — The Next Frontier",
    titleLines: ["HSBC —", "The Next Frontier"],
    category: "Campaign / Art Direction / Concept Design",
    tag: "Student concept",
    note: "Student concept responding to a D&AD New Blood Awards brief. Not an official HSBC campaign and not an award-winning D&AD entry.",
    summary:
      "A speculative campaign reimagining HSBC's iconic red as a symbol of exploration and future ambition.",
    meta: [
      { label: "Discipline", value: "Campaign / Art Direction / Concept Design" },
      { label: "Project type", value: "Student concept — D&AD New Blood brief response" },
      { label: "Deliverables", value: "Campaign posters, rocket concept, Frontier Card, mission patch" },
    ],
    cover: { desc: "Cover — mission patch", ratio: "4/5", src: "/work/hsbc/07.webp", position: "0% 50%", alt: "Embroidered The Next Frontier HSBC mission patch on a red astronaut suit" },
    hero: { desc: "Hero — campaign key visual", ratio: "16/9", src: "/work/hsbc/01.webp", alt: "The Next Frontier HSBC mission emblem set against a deep red starfield" },
    sections: [
      {
        id: "overview",
        title: "Overview",
        body: [
          "A speculative response to a D&AD New Blood Awards brief. The campaign reimagines HSBC's iconic red as a symbol of exploration and future ambition, with campaign posters, a branded rocket concept, an imagined Frontier Card and astronaut mission patch.",
        ],
      },
      {
        id: "brief",
        title: "The Brief",
        body: [
          "Problem — HSBC operates globally, but its most recognisable asset, HSBC Red, exists in markets where red is everywhere. In finance and culture the colour is widely used, making it difficult for HSBC to truly own it.",
          "Insight — History celebrates explorers. But every frontier, from oceans to space, was made possible by the capital behind it.",
        ],
        layout: "full",
        media: [
          { desc: "Problem, insight, solution", ratio: "16/9", src: "/work/hsbc/02.webp", alt: "Three panels — Problem, Insight and Solution — moving from a black starfield to HSBC red" },
        ],
      },
      {
        id: "idea",
        title: "Creative Idea",
        statement: "Funding the Next Frontier.",
        body: [
          "HSBC redefines its brand colour by turning HSBC Red into the colour of expansion.",
          "Inspired by space exploration, the visual system presents HSBC as the financial infrastructure behind humanity's next frontier.",
        ],
        layout: "full",
        media: [
          { desc: "Concept — The Next Frontier emblem", ratio: "16/9", src: "/work/hsbc/03.webp", alt: "Concept slide with The Next Frontier HSBC emblem" },
        ],
      },
      {
        id: "campaign",
        title: "Campaign Visuals",
        body: ["A global poster campaign: Every frontier needs funding."],
        layout: "full",
        media: [
          { desc: "Global poster campaign", ratio: "16/9", src: "/work/hsbc/04.webp", alt: "Two campaign posters in a gallery space — a red rocket launch reading Every Frontier Needs Funding, and an astronaut reading Funding the Next Frontier" },
        ],
      },
      {
        id: "applications",
        title: "Brand Applications",
        body: ["A branded rocket concept, an imagined Frontier Card and an astronaut mission patch."],
        layout: "stack",
        media: [
          { desc: "Branded rocket concept", ratio: "16/9", src: "/work/hsbc/05.webp", alt: "Red HSBC-branded rocket on a launch tower beside front and side views of the rocket" },
          { desc: "Frontier Card (imagined)", ratio: "16/9", src: "/work/hsbc/06.webp", alt: "Imagined limited-edition Frontier Card shown floating in multiples beside a red panel describing it" },
          { desc: "Astronaut mission patch", ratio: "16/9", src: "/work/hsbc/07.webp", alt: "Embroidered The Next Frontier mission patch on a suit and as a standalone badge" },
        ],
      },
    ],
  },
  {
    slug: "studio-moross-concert-motion",
    no: "04",
    title: "Studio Moross — Concert Motion Design",
    titleLines: ["Studio Moross —", "Concert Motion"],
    category: "Motion Design / Creative Technology",
    tag: "University brief",
    note: "University industry brief set by Studio Moross. Not commissioned work, and not deployed at a real concert.",
    summary:
      "Life Music Visuals — a three-screen, sound-reactive concert visual for The Weeknd's Blinding Lights, made in TouchDesigner and After Effects for a university industry brief.",
    meta: [
      { label: "Discipline", value: "Motion Design / Creative Technology" },
      { label: "Project type", value: "University industry brief set by Studio Moross" },
      { label: "Track", value: "The Weeknd — Blinding Lights" },
      { label: "Tools", value: "TouchDesigner, Adobe After Effects" },
    ],
    cover: { kind: "video", desc: "Cover — motion loop", ratio: "4/5", loop: "/work/moross/cover-loop.mp4", poster: "/work/moross/cover-poster.webp", alt: "Looping glowing red light form from the centre screen" },
    hero: { kind: "video", desc: "Showreel — three-screen loop", ratio: "16/3", loop: "/work/moross/showreel.mp4", poster: "/work/moross/showreel-poster.webp", alt: "Three-screen concert visual: fiery distortion on the left, a glowing light form in the centre, blue static on the right" },
    sections: [
      {
        id: "overview",
        title: "Overview",
        statement: "Life Music Visuals — Blinded by the Lights.",
        body: [
          "A university industry brief set by Studio Moross, exploring concert-focused motion graphics using Adobe After Effects and TouchDesigner. The piece is a three-screen visual for The Weeknd's Blinding Lights.",
        ],
        layout: "full",
        media: [
          { desc: "Title slide", ratio: "16/9", src: "/work/moross/01.webp", alt: "Title slide: Life Music Visuals — Blinded by the Lights, The Weeknd, made by Dzhumagaziev Eldiiar" },
        ],
      },
      {
        id: "concept",
        title: "Concept",
        body: [
          "The concept explores the emotional conflict of a person caught between constant euphoria and the emptiness that follows it.",
          "The three screens represent this inner struggle: the left visualises euphoria and excess, the right shows darkness and emotional void, and the centre portrays the soul trying to stay balanced between both sides.",
        ],
        layout: "full",
        media: [
          { desc: "Concept slide", ratio: "16/9", src: "/work/moross/02.webp", alt: "Concept slide showing the three screens — fiery left, TOUCH in the centre, blue static on the right — above the concept text" },
        ],
      },
      {
        id: "exploration",
        title: "Motion Exploration",
        body: [
          "The visual language is built on the contrast between artificial euphoria and emotional emptiness — neon light, human silhouettes, blur and distortion.",
          "Warm tones of orange, red and yellow denote euphoria and overstimulation, while cold blues and violets indicate emptiness and detachment.",
        ],
        layout: "stack3",
        media: [
          { desc: "Visual language slide", ratio: "16/9", src: "/work/moross/03.webp", alt: "Visual language slide with a grid of neon, blurred and distorted reference images" },
          { kind: "video", desc: "Left screen — euphoria", ratio: "16/9", loop: "/work/moross/screen-0.mp4", poster: "/work/moross/screen-0-poster.webp", alt: "Left screen loop: fiery orange distortion over deep blue" },
          { kind: "video", desc: "Centre screen — the soul", ratio: "16/9", loop: "/work/moross/screen-1.mp4", poster: "/work/moross/screen-1-poster.webp", alt: "Centre screen loop: a glowing light form shifting between red and blue" },
          { kind: "video", desc: "Right screen — the void", ratio: "16/9", loop: "/work/moross/screen-2.mp4", poster: "/work/moross/screen-2-poster.webp", alt: "Right screen loop: cold blue static and white light" },
        ],
      },
      {
        id: "process",
        title: "Creative Process",
        body: [
          "Most of the visuals were produced in TouchDesigner, generating light-based forms that respond directly to the music's frequency and amplitude, tuned to the track's rhythm and energy.",
          "Adobe After Effects was used to refine composition, enhance glow and apply colour grading and distortion mapping, creating smooth transitions and a visual rhythm across the three screens.",
        ],
        layout: "asym",
        media: [
          { desc: "TouchDesigner network and After Effects", ratio: "16/9", src: "/work/moross/04.webp", alt: "Process slide showing a TouchDesigner node network and an After Effects workspace" },
          { desc: "Moodboard", ratio: "934/747", src: "/work/moross/moodboard.webp", alt: "Moodboard of neon gradients, blurred silhouettes, crowds and brain-scan imagery" },
        ],
      },
      {
        id: "highlights",
        title: "Highlights",
        body: [
          "The transition from the eye into a tunnel of letter 'o's plays directly with the 'Blinded by the Lights' text. The left screen became the strongest of the three.",
          "An interactive 'touch' moment lets the viewer literally punch through the screen.",
        ],
        layout: "full",
        media: [
          { desc: "Highlights slide", ratio: "16/9", src: "/work/moross/05.webp", alt: "Highlights slide with stills of I'm Blinded, By the Lights, TOUCH and the glowing o tunnel" },
        ],
      },
      {
        id: "final",
        title: "Final Motion Work",
        body: ["The full three-screen piece, with sound."],
        layout: "full",
        media: [{ kind: "video", desc: "Final motion work", ratio: "16/3", video: "/work/moross/showreel.mp4", poster: "/work/moross/showreel-poster.webp", alt: "Full three-screen motion piece for Blinding Lights" }],
      },
    ],
  },
];

const numberMedia = (project) => {
  const count = { image: 0, video: 0 };
  const tagged = (m) => {
    const kind = m.kind || "image";
    count[kind] += 1;
    return { ...m, kind, n: count[kind] };
  };
  const hero = tagged(project.hero);
  const sections = project.sections.map((s) => ({ ...s, media: (s.media || []).map(tagged) }));
  return { ...project, cover: { kind: "image", ...project.cover }, hero, sections };
};

export const PROJECTS = RAW.map(numberMedia);
export const getProject = (slug) => PROJECTS.find((p) => p.slug === slug);
